// Pile d'annulation générique (annuler / rétablir), pure et testable, utilisée par le canevas de
// l'éditeur de mise en page sur toutes les modifications de l'arbre.
//
// Les états sont comparés par leur sérialisation JSON : une modification qui ne change rien n'est pas
// empilée. Les modifications rapprochées de même clé (frappe dans un champ du panneau, glissement
// d'une poignée) sont fusionnées en une seule entrée.

export interface History<T> {
  present: T;
  past: T[];
  future: T[];
  lastKey: string | null;
  lastAt: number;
}

export const COALESCE_MS = 800;

export function createHistory<T>(present: T): History<T> {
  return { present, past: [], future: [], lastKey: null, lastAt: 0 };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Enregistre un nouvel état. `key` fusionne avec l'entrée précédente si elle porte la même clé et date de moins de COALESCE_MS. */
export function commit<T>(h: History<T>, next: T, key: string | null = null, now = Date.now()): History<T> {
  if (same(h.present, next)) return h;
  const coalesce = key !== null && key === h.lastKey && now - h.lastAt < COALESCE_MS && h.past.length > 0;
  return {
    present: next,
    past: coalesce ? h.past : [...h.past, h.present],
    future: [],
    lastKey: key,
    lastAt: now,
  };
}

export const canUndo = <T>(h: History<T>) => h.past.length > 0;
export const canRedo = <T>(h: History<T>) => h.future.length > 0;

export function undo<T>(h: History<T>): History<T> {
  if (!canUndo(h)) return h;
  const past = h.past.slice(0, -1);
  return { present: h.past[h.past.length - 1], past, future: [h.present, ...h.future], lastKey: null, lastAt: 0 };
}

export function redo<T>(h: History<T>): History<T> {
  if (!canRedo(h)) return h;
  const [next, ...future] = h.future;
  return { present: next, past: [...h.past, h.present], future, lastKey: null, lastAt: 0 };
}
